<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">&nbsp;</div>
                <div class="page-bar-main">&nbsp;</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="ai-trainer">

                <div v-if="denied" class="ai-denied">
                    <span class="ai-icon-badge" style="margin-bottom:14px">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z"/></svg>
                    </span>
                    <h2>AI Training is restricted</h2>
                    <p>Only your company's designated AI trainer can view or change the AI's training.<br/>The rules they set are applied automatically whenever you use the AI features.</p>
                    <div class="ai-btn ai-btn-ghost" style="margin-top:16px" @click="$router.push('/ai-hub')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11v2H8l5.5 5.5-1.42 1.42L4.16 12l7.92-7.92L13.5 5.5 8 11h12z"/></svg>
                        Back to The Forge
                    </div>
                </div>

                <template v-else-if="loaded">
                    <div class="ai-hero" style="margin-bottom:20px">
                        <div class="ai-hero-left">
                            <span class="ai-icon-badge">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg>
                            </span>
                            <div>
                                <div class="ai-hero-title">Train Your AI <span class="ai-tag">AI</span></div>
                                <p class="ai-hero-sub">
                                    Tell the AI how you want your company's processes written — your standards, your style,
                                    your non-negotiables. It remembers everything here and applies it every time anyone on
                                    your team uses the AI. Only you can change this training.
                                </p>
                            </div>
                        </div>
                        <div class="ai-btn ai-btn-ghost" style="align-self:flex-start;white-space:nowrap" @click="$router.push('/ai-hub')">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11v2H8l5.5 5.5-1.42 1.42L4.16 12l7.92-7.92L13.5 5.5 8 11h12z"/></svg>
                            Back to The Forge
                        </div>
                    </div>

                <section v-if="competitiveAllowed" class="competitive-card" aria-labelledby="competitive-title">
                    <div>
                        <div class="competitive-eyebrow">IronBook market research</div>
                        <h2 id="competitive-title">Competitive analysis</h2>
                        <p v-if="competitive">{{ competitiveDate }} · {{ competitive.changes }} updates in this edition</p>
                        <p v-else>{{ competitiveError ? 'The latest edition could not be checked.' : 'Weekly research, product comparisons and past editions.' }}</p>
                        <p v-if="competitive?.headline" class="competitive-sub">{{ competitive.headline }}</p>
                    </div>
                    <router-link to="/competitive-analysis" class="ai-btn competitive-link">View report <span aria-hidden="true">→</span></router-link>
                </section>

                <section v-if="usageAllowed" class="competitive-card" aria-labelledby="usage-title">
                    <div>
                        <div class="competitive-eyebrow">IronBook operations</div>
                        <h2 id="usage-title">IronBook usage</h2>
                        <p>How many people use IronBook in each company: logins, active users, content, sign-offs, meetings and AI use.</p>
                    </div>
                    <router-link to="/usage" class="ai-btn competitive-link">View usage <span aria-hidden="true">→</span></router-link>
                </section>

                    <div class="ai-trainer-cols">
                        <!-- Chat -->
                        <div class="ai-chat-panel">
                            <div class="ai-chat-scroll" ref="chatScroll">
                                <div v-if="!history.length" class="ai-chat-empty">
                                    <p><strong>Start training.</strong> Try things like:</p>
                                    <p class="ai-chat-hint">"Every process must end with an escalation contact section"</p>
                                    <p class="ai-chat-hint">"Keep language professional — never use casual terms"</p>
                                    <p class="ai-chat-hint">"Safety warnings always come before the step they apply to"</p>
                                </div>
                                <div v-for="(m, i) in history" :key="i" class="ai-msg" :class="m.Role == 'user' ? 'ai-msg-user' : 'ai-msg-ai'">
                                    <div class="ai-msg-bubble">{{ m.Text }}</div>
                                </div>
                                <div v-if="thinking" class="ai-msg ai-msg-ai">
                                    <div class="ai-msg-bubble ai-msg-thinking">
                                        <span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span>
                                    </div>
                                </div>
                            </div>
                            <div class="ai-chat-inputrow">
                                <div style="position:relative;flex:1;display:flex">
                                    <textarea v-model="draft" rows="2" placeholder="Tell the AI how you want things done..."
                                        style="padding-right:44px" @keydown.enter.exact.prevent="send()"></textarea>
                                    <ai-dictation style="position:absolute;right:10px;bottom:10px" @text="draft = (draft ? draft.trim() + ' ' : '') + $event" />
                                </div>
                                <div class="ai-btn" :class="(!draft.trim() || thinking) ? 'disable-button' : ''" @click="send()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                                    Send
                                </div>
                            </div>
                        </div>

                        <!-- Rules -->
                        <div class="ai-rules-panel">
                            <template v-if="learned.length">
                                <div class="ai-rules-head">
                                    <div class="ai-section-label" style="margin:0">Learned from your team's feedback</div>
                                    <span class="ai-learned-count">{{ learned.length }} suggested</span>
                                </div>
                                <div class="ai-rules-empty" style="padding-top:0;padding-bottom:10px">
                                    When anyone asks the AI to change a draft, it watches for standing preferences.
                                    Approve to make them permanent rules — or dismiss.
                                </div>
                                <div v-for="s in learned" :key="'l'+s.ID" class="ai-rule ai-rule-suggested">
                                    <span class="ai-rule-num ai-rule-num-suggested">?</span>
                                    <span class="ai-rule-text">
                                        {{ s.Rule }}
                                        <span class="ai-learned-src">from {{ s.Source }}</span>
                                    </span>
                                    <span class="ai-rule-del" style="color:#16a34a" title="Approve — becomes a live rule" @click="resolveLearned(s, 'approve')">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                                    </span>
                                    <span class="ai-rule-del" title="Dismiss" @click="resolveLearned(s, 'dismiss')">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
                                    </span>
                                </div>
                                <div style="height:14px"></div>
                            </template>
                            <div class="ai-rules-head">
                                <div class="ai-section-label" style="margin:0">Active training rules</div>
                                <span v-if="rules.length" class="ai-live-dot">● live for all staff</span>
                            </div>
                            <div v-if="!rules.length" class="ai-rules-empty">
                                No rules yet — everything you teach the AI in the chat shows up here.
                            </div>
                            <div v-for="(rule, i) in rules" :key="'r'+i" class="ai-rule">
                                <span class="ai-rule-num">{{ i + 1 }}</span>
                                <textarea v-if="editing == 'rules:'+i" v-model="editText" class="ai-edit-input" rows="2" @keydown.enter.exact.prevent="saveEdit('rules', i)"></textarea>
                                <span v-else class="ai-rule-text" title="Click to edit" style="cursor:text" @click="startEdit('rules', i, rule)">{{ rule }}</span>
                                <span v-if="editing == 'rules:'+i" class="ai-rule-del" style="color:#16a34a" title="Save" @click="saveEdit('rules', i)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                                </span>
                                <span v-else class="ai-rule-del" title="Remove rule" @click="editList('rules', 'delete', i)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
                                </span>
                            </div>
                            <div style="display:flex;gap:8px;margin-top:8px">
                                <input v-model="newRule" type="text" placeholder="Type a rule directly..." style="flex:1;margin:0" @keydown.enter="addItem('rules')" />
                                <div class="ai-btn ai-btn-ghost" :class="!newRule.trim() ? 'disable-button' : ''" @click="addItem('rules')">Add</div>
                            </div>
                            <div v-if="rules.length" class="ai-rules-note">
                                These rules are enforced on every AI-generated or AI-improved process, for every user. Click a rule to edit it.
                            </div>

                            <div class="ai-rules-head" style="margin-top:22px">
                                <div class="ai-section-label" style="margin:0">Company knowledge</div>
                            </div>
                            <div v-if="!knowledge.length" class="ai-rules-empty">
                                Facts the AI should know — products, equipment, systems, roles, terminology. Learned from your chats, or add them directly.
                            </div>
                            <div v-for="(fact, i) in knowledge" :key="'k'+i" class="ai-rule">
                                <span class="ai-rule-num" style="background:white;color:var(--ai-accent);border:1px solid var(--ai-border-soft)">&#9432;</span>
                                <textarea v-if="editing == 'knowledge:'+i" v-model="editText" class="ai-edit-input" rows="2" @keydown.enter.exact.prevent="saveEdit('knowledge', i)"></textarea>
                                <span v-else class="ai-rule-text" title="Click to edit" style="cursor:text" @click="startEdit('knowledge', i, fact)">{{ fact }}</span>
                                <span v-if="editing == 'knowledge:'+i" class="ai-rule-del" style="color:#16a34a" title="Save" @click="saveEdit('knowledge', i)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/></svg>
                                </span>
                                <span v-else class="ai-rule-del" title="Remove" @click="editList('knowledge', 'delete', i)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
                                </span>
                            </div>
                            <div style="display:flex;gap:8px;margin-top:8px">
                                <input v-model="newFact" type="text" placeholder="Add a fact the AI should know..." style="flex:1;margin:0" @keydown.enter="addItem('knowledge')" />
                                <div class="ai-btn ai-btn-ghost" :class="!newFact.trim() ? 'disable-button' : ''" @click="addItem('knowledge')">Add</div>
                            </div>

                            <div class="ai-rules-head" style="margin-top:22px">
                                <div class="ai-section-label" style="margin:0">Style guide</div>
                            </div>
                            <div class="ai-rules-empty" style="padding-bottom:8px">
                                How your processes should be written — tone, voice, wording, structure. The AI follows this
                                every time it writes, improves or audits a process.
                            </div>
                            <textarea v-model="style" class="ai-style-input" rows="6"
                                placeholder="e.g. Write in third person — never 'I'. Keep steps short and direct. Say 'team member', not 'employee'. Imperial units only. Headers: Overview first, then Step 1, Step 2..."></textarea>
                            <div style="display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-top:8px">
                                <span v-if="styleSaved" style="color:#16a34a;font-size:12.5px;font-weight:600">Saved</span>
                                <div class="ai-btn ai-btn-ghost" :class="style == savedStyle ? 'disable-button' : ''" @click="saveStyle()">Save Style Guide</div>
                            </div>

                            <div class="ai-rules-head" style="margin-top:22px">
                                <div class="ai-section-label" style="margin:0">Who can train the AI</div>
                            </div>
                            <div v-for="t in trainers" :key="t.ID" class="ai-rule" style="align-items:center">
                                <span class="ai-rule-text">{{ t.Email }}</span>
                                <span v-if="trainers.length > 1" class="ai-rule-del" title="Remove trainer" @click="removeTrainerCheck(t)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/></svg>
                                </span>
                            </div>
                            <div style="display:flex;gap:8px;margin-top:8px">
                                <input v-model="newTrainerEmail" type="email" placeholder="email@company.com" style="flex:1;margin:0" @keydown.enter="addTrainer()" />
                                <div class="ai-btn ai-btn-ghost" :class="!newTrainerEmail.trim() ? 'disable-button' : ''" @click="addTrainer()">Add</div>
                            </div>
                            <div class="ai-rules-note">Trainers can change the AI's rules and manage this list. Everyone else can use the AI but not retrain it.</div>
                        </div>
                    </div>
                </template>
            </div>

            <confirm-delete v-if="showTrainerToRemoveConfirm" @confirm="removeTrainer()" @cancel="() => { showTrainerToRemoveConfirm = false; }" :msg="trainerToRemoveMsg"/>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIDictation from '@/components/AIDictation.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';

export default defineComponent({
    name: 'AITrainer',
    components: { 
        AiDictation: AIDictation,
        ConfirmDelete,
    },
    data() {
        return {
            loaded: false,
            denied: false,
            history: [] as any[],
            rules: [] as string[],
            draft: '',
            thinking: false,
            trainers: [] as any[],
            newTrainerEmail: '',
            knowledge: [] as string[],
            style: '',
            savedStyle: '',
            styleSaved: false,
            newRule: '',
            newFact: '',
            editing: null as any,
            editText: '',
            learned: [] as any[],
            trainerToRemove: null as any,
            trainerToRemoveMsg: '',
            showTrainerToRemoveConfirm: false,
            competitive: null as any,
            competitiveAllowed: false,
            usageAllowed: false,
            competitiveError: false
        }
    },
    computed: {
        competitiveDate(): string {
            const d = this.competitive?.date;
            if (!d) return '';
            const [y, m, day] = d.split('-').map(Number);
            return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
        }
    },
    async created() {
        await axios.get(`${this.$store.state.apiUrl}/api/ai/training/chat`)
            .then(async resp => {
                this.history = resp.data.history;
                this.rules = resp.data.rules;
                this.knowledge = resp.data.knowledge || [];
                await axios.get(`${this.$store.state.apiUrl}/api/ai/training`)
                    .then(r => { this.style = r.data.style || ''; this.savedStyle = this.style; })
                    .catch(e => console.log(e));
                await axios.get(`${this.$store.state.apiUrl}/api/ai/training/trainers`)
                    .then(r => { this.trainers = r.data; })
                    .catch(e => console.log(e));
                await axios.get(`${this.$store.state.apiUrl}/api/ai/training/learned`)
                    .then(r => { this.learned = r.data; })
                    .catch(e => console.log(e));
            })
            .catch(e => {
                if (e?.response?.status == 403) {
                    this.denied = true;
                } else {
                    console.log(e);
                }
            });
        if (this.$store.state.user.superAdmin) {
            axios.get(`${this.$store.state.apiUrl}/api/competitive/access`)
                .then(async r => {
                    this.competitiveAllowed = r.data.allowed === true;
                    if (this.competitiveAllowed) {
                        try { this.competitive = (await axios.get(`${this.$store.state.apiUrl}/api/competitive/latest`)).data; }
                        catch { this.competitiveError = true; }
                    }
                }).catch(() => { /* Keep AI training available if report status cannot load. */ });
            axios.get(`${this.$store.state.apiUrl}/api/usage/access`)
                .then(r => { this.usageAllowed = r.data?.allowed === true; })
                .catch(() => { /* card only */ });
        }
        this.loaded = true;
        this.$store.state.routeLoaded = true;
        this.scrollChat();
    },
    methods: {
        async saveStyle() {
            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/style/edit`, { text: this.style })
                .then(resp => {
                    this.style = resp.data.style;
                    this.savedStyle = this.style;
                    this.styleSaved = true;
                    setTimeout(() => { this.styleSaved = false; }, 2500);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error saving the style guide.');
                });
        },
        async send() {
            const message = this.draft.trim();
            if (!message || this.thinking) return;
            this.draft = '';
            this.history.push({ Role: 'user', Text: message });
            this.thinking = true;
            this.scrollChat();

            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/chat`, { message })
                .then(resp => {
                    this.history.push({ Role: 'assistant', Text: resp.data.reply });
                    this.rules = resp.data.rules;
                    if (resp.data.knowledge) this.knowledge = resp.data.knowledge;
                })
                .catch(e => {
                    this.history.push({ Role: 'assistant', Text: e?.response?.data || 'Something went wrong — please try again.' });
                });
            this.thinking = false;
            this.scrollChat();
        },
        async editList(list: string, action: string, index: number | null = null, text: string | null = null) {
            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/${list}/edit`, { action, index, text })
                .then(resp => {
                    this.rules = resp.data.rules;
                    this.knowledge = resp.data.knowledge;
                })
                .catch(e => alert(e?.response?.data || 'Could not save'));
        },
        startEdit(list: string, index: number, current: string) {
            this.editing = `${list}:${index}`;
            this.editText = current;
        },
        async saveEdit(list: string, index: number) {
            const text = this.editText.trim();
            this.editing = null;
            if (text) await this.editList(list, 'update', index, text);
        },
        async addItem(list: string) {
            const field = list == 'rules' ? 'newRule' : 'newFact';
            const text = (this as any)[field].trim();
            if (!text) return;
            (this as any)[field] = '';
            await this.editList(list, 'add', null, text);
        },
        async addTrainer() {
            const email = this.newTrainerEmail.trim();
            if (!email) return;
            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/trainers`, { email })
                .then(resp => {
                    this.trainers = resp.data;
                    this.newTrainerEmail = '';
                })
                .catch(e => alert(e?.response?.data || 'Could not add trainer'));
        },
        async resolveLearned(s: any, action: string) {
            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/learned/${s.ID}/resolve`, { action })
                .then(resp => {
                    this.learned = this.learned.filter((l: any) => l.ID != s.ID);
                    if (resp.data.rules) this.rules = resp.data.rules;
                })
                .catch(e => alert(e?.response?.data || 'Could not save'));
        },
        removeTrainerCheck(t: any) {
            this.trainerToRemove = t;
            this.trainerToRemoveMsg = `Remove ${t.Email} as an AI trainer?`;
            this.showTrainerToRemoveConfirm = true;
        },
        async removeTrainer() {
            this.showTrainerToRemoveConfirm = false;
            await axios.post(`${this.$store.state.apiUrl}/api/ai/training/trainers/${this.trainerToRemove.ID}/delete`, {})
                .then(resp => { this.trainers = resp.data; })
                .catch(e => alert(e?.response?.data || 'Could not remove trainer'));
        },
        scrollChat() {
            this.$nextTick(() => {
                const el = this.$refs.chatScroll as HTMLElement;
                if (el) el.scrollTop = el.scrollHeight;
            });
        }
    }
});
</script>

<style scoped>
.competitive-card { display:flex; align-items:center; justify-content:space-between; gap:20px; padding:24px; margin:24px 0; background:#fffaf5; border:1px solid #e8cbb4; border-left:4px solid #ef7d26; border-radius:12px; }
.competitive-eyebrow { color:#985018; font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; }
.competitive-card h2 { margin:5px 0; font-size:21px; }
.competitive-card p { margin:5px 0 0; color:#6c625b; font-size:13px; }
.competitive-sub { max-width:70ch; }
.competitive-link { text-decoration:none; white-space:nowrap; flex-shrink:0; }
@media(max-width:600px) { .competitive-card { align-items:flex-start; flex-direction:column; padding:18px; gap:14px; } }

.ai-trainer { max-width: 1150px; margin: 0 auto; padding: 20px 10px 60px; }
.ai-denied { text-align: center; padding: 90px 20px; color: #6f655c; }
.ai-denied h2 { color: #1f1712; margin: 0 0 10px; }
.ai-trainer-cols { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; align-items: start; }
@media (max-width: 980px) { .ai-trainer-cols { grid-template-columns: 1fr; } }

.ai-chat-panel {
    background: white; border: 1px solid #f6e3d3; border-radius: 16px;
    box-shadow: var(--ai-shadow-card); display: flex; flex-direction: column;
    height: 560px; overflow: hidden;
}
.ai-chat-scroll { flex: 1; overflow-y: auto; padding: 20px; }
.ai-chat-empty { color: #a0958a; padding: 30px 10px; }
.ai-chat-hint { background: var(--ai-grad-soft); border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 8px 14px; font-style: italic; color: #7a5a45; }
.ai-msg { display: flex; margin-bottom: 12px; }
.ai-msg-user { justify-content: flex-end; }
.ai-msg-bubble {
    max-width: 82%; padding: 10px 15px; border-radius: 16px; font-size: 14.5px;
    line-height: 1.5; white-space: pre-wrap; word-break: break-word;
}
.ai-msg-user .ai-msg-bubble { background: var(--ai-grad); color: white; border-bottom-right-radius: 5px; box-shadow: 0 3px 10px rgba(234, 88, 12, .25); }
.ai-msg-ai .ai-msg-bubble { background: #fdf3ea; color: #2b221b; border-bottom-left-radius: 5px; }
.ai-msg-thinking { display: flex; gap: 5px; align-items: center; padding: 14px 18px; }
.ai-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--ai-accent); opacity: .4; animation: ai-blink 1.2s infinite; }
.ai-dot:nth-child(2) { animation-delay: .2s; }
.ai-dot:nth-child(3) { animation-delay: .4s; }
@keyframes ai-blink { 0%, 100% { opacity: .25; } 50% { opacity: 1; } }
.ai-chat-inputrow { display: flex; gap: 10px; padding: 14px; border-top: 1px solid #f9ece0; align-items: flex-end; background: #fffcf9; }
.ai-chat-inputrow textarea {
    flex: 1; resize: none; border: 1px solid var(--ai-border-soft); border-radius: 10px;
    padding: 10px 12px; font-size: 14px; font-family: inherit; margin: 0;
}
.ai-chat-inputrow textarea:focus { outline: none; border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .15); }

.ai-rules-panel {
    background: white; border: 1px solid #f6e3d3; border-radius: 16px;
    box-shadow: var(--ai-shadow-card); padding: 18px; max-height: 560px; overflow-y: auto;
}
.ai-rules-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.ai-style-input {
    width: 100%; box-sizing: border-box; resize: vertical; margin: 0;
    border: 1px solid #f6e3d3; border-radius: 12px; padding: 12px 14px;
    background: #fffcf9; font-size: 14px; color: #2b221b; font-family: inherit; line-height: 1.5;
}
.ai-style-input:focus { outline: none; border-color: #fdba74; box-shadow: 0 0 0 3px rgba(249, 115, 22, .12); }
.ai-live-dot { color: #16a34a; font-size: 12px; font-weight: 600; }
.ai-rules-empty { color: #a0958a; font-size: 14px; padding: 16px 4px; }
.ai-rule {
    display: flex; gap: 10px; align-items: flex-start; padding: 11px 12px; margin-bottom: 8px;
    background: linear-gradient(135deg, #fffaf5, #fff6ee); border: 1px solid var(--ai-border-soft);
    border-radius: 12px; transition: all .25s ease;
}
.ai-rule-num {
    width: 22px; height: 22px; border-radius: 7px; background: var(--ai-grad); color: white;
    font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ai-rule-text { flex: 1; font-size: 13.5px; line-height: 1.45; color: #2b221b; }
.ai-edit-input { flex: 1; border: 1px solid #fdba74; border-radius: 8px; padding: 6px 8px; font-size: 13.5px; font-family: inherit; margin: 0; resize: vertical; }
.ai-rule-del { color: #d9c3b0; cursor: pointer; flex-shrink: 0; padding-top: 2px; }
.ai-rule-del:hover { color: #ef4444; }
.ai-rules-note { font-size: 12px; color: #a0958a; margin-top: 10px; font-style: italic; }
.ai-rule-enter-active, .ai-rule-leave-active { transition: all .3s ease; }
.ai-rule-enter-from, .ai-rule-leave-to { opacity: 0; transform: translateY(-6px); }
.ai-section-label { font-weight: 700; text-transform: uppercase; font-size: 11.5px; letter-spacing: .08em; color: #a0958a; }
.ai-rule-suggested { border-style: dashed; background: #fffdf5; border-color: #fde68a; }
.ai-rule-num-suggested { background: #fef9c3; color: #a16207; border: 1px solid #fde047; }
.ai-learned-src { display: block; font-size: 11.5px; color: #a0958a; margin-top: 3px; font-style: italic; }
.ai-learned-count { color: #a16207; font-size: 12px; font-weight: 700; background: #fef9c3; border: 1px solid #fde047; border-radius: 12px; padding: 1px 10px; }
</style>
